import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { getContacts, exportContacts } from "services/contacts";
import logger from "utils/logger";

export default function CardContactsTable({ color, onViewContact }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterUserType, setFilterUserType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(15);

  useEffect(() => {
    loadContacts();
  }, [searchTerm, filterType, filterUserType, startDate, endDate, currentPage]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const filters = {
        page: currentPage,
        per_page: perPage
      };

      if (searchTerm) filters.search = searchTerm;
      if (filterType) filters.type = filterType;
      if (filterUserType) filters.user_type = filterUserType;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      const response = await getContacts(filters);
      setContacts(response.data.data || []);
      setTotalPages(response.data.last_page || 1);
      setError(null);
    } catch (err) {
      logger.error("Error loading contacts:", err);
      setError("Error al cargar los contactos");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (filterType) filters.type = filterType;
      if (filterUserType) filters.user_type = filterUserType;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      await exportContacts(filters);
    } catch (err) {
      logger.error("Error exporting contacts:", err);
      alert("Error al exportar los contactos");
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterType("");
    setFilterUserType("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const getUserTypeBadge = (userType) => {
    if (!userType) return <span className="text-blueGray-400 text-xs">No especificado</span>;

    const badges = {
      solo_developer: { color: "bg-blue-500", text: "Solo Developer" },
      entrepreneur: { color: "bg-green-500", text: "Entrepreneur" },
      startup_team: { color: "bg-purple-500", text: "Startup Team" }
    };

    const badge = badges[userType] || { color: "bg-blueGray-500", text: userType };

    return (
      <span className={`${badge.color} text-white text-xs px-2 py-1 rounded-full`}>
        {badge.text}
      </span>
    );
  };

  const getContactTypeBadge = (type) => {
    const badges = {
      contact_form: { color: "bg-lightBlue-500", text: "Contacto", icon: "fa-envelope" },
      quick_signup: { color: "bg-orange-500", text: "Suscripción", icon: "fa-bolt" }
    };

    const badge = badges[type] || { color: "bg-blueGray-500", text: type, icon: "fa-question" };

    return (
      <span className={`${badge.color} text-white text-xs px-2 py-1 rounded-full`}>
        <i className={`fas ${badge.icon} mr-1`}></i>
        {badge.text}
      </span>
    );
  };

  const getDaysSinceBadge = (days) => {
    let color = "bg-green-500";
    if (days > 7) color = "bg-orange-500";
    if (days > 14) color = "bg-red-500";

    return (
      <span className={`${color} text-white text-xs px-2 py-1 rounded-full`}>
        {days} {days === 1 ? 'día' : 'días'}
      </span>
    );
  };

  const truncateMessage = (message, maxLength = 50) => {
    if (!message) return "—";
    return message.length > maxLength ? message.substring(0, maxLength) + "..." : message;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <div
        className={
          "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
          (color === "light" ? "bg-white" : "bg-lightBlue-900 text-white")
        }
      >
        <div className="rounded-t mb-0 px-4 py-3 border-0">
          <div className="flex flex-wrap items-center">
            <div className="relative w-full px-4 max-w-full flex-grow flex-1">
              <h3
                className={
                  "font-semibold text-lg " +
                  (color === "light" ? "text-blueGray-700" : "text-white")
                }
              >
                <i className="fas fa-address-book mr-2"></i>
                Gestión de Leads
              </h3>
            </div>
            <div className="relative w-full px-4 max-w-full flex-grow flex-1 text-right">
              <button
                className={`text-white text-xs font-bold uppercase px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 ${
                  exporting
                    ? "bg-blueGray-400 cursor-not-allowed"
                    : "bg-emerald-500 active:bg-emerald-600 hover:bg-emerald-600"
                }`}
                type="button"
                onClick={handleExport}
                disabled={exporting}
              >
                <i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-download'} mr-1`}></i>
                {exporting ? 'Exportando...' : 'Exportar CSV'}
              </button>
            </div>
          </div>

          {/* Search and filters */}
          <div className="flex flex-wrap items-center mt-4 gap-2">
            <div className="relative flex-grow min-w-[200px]">
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                className="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="relative">
              <select
                className="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring ease-linear transition-all duration-150"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="">Todos los tipos</option>
                <option value="contact_form">Contacto</option>
                <option value="quick_signup">Suscripción</option>
              </select>
            </div>
            <div className="relative">
              <select
                className="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring ease-linear transition-all duration-150"
                value={filterUserType}
                onChange={(e) => setFilterUserType(e.target.value)}
              >
                <option value="">Todos los perfiles</option>
                <option value="solo_developer">Solo Developer</option>
                <option value="entrepreneur">Entrepreneur</option>
                <option value="startup_team">Startup Team</option>
              </select>
            </div>
            <div className="relative">
              <input
                type="date"
                className="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring ease-linear transition-all duration-150"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Desde"
              />
            </div>
            <div className="relative">
              <input
                type="date"
                className="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring ease-linear transition-all duration-150"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="Hasta"
              />
            </div>
            {(searchTerm || filterType || filterUserType || startDate || endDate) && (
              <button
                className="bg-blueGray-500 text-white text-xs font-bold uppercase px-3 py-2 rounded hover:bg-blueGray-600"
                onClick={clearFilters}
              >
                <i className="fas fa-times mr-1"></i>
                Limpiar
              </button>
            )}
          </div>
        </div>

        <div className="block w-full overflow-x-auto">
          {loading ? (
            <div className="text-center py-8">
              <i className="fas fa-spinner fa-spin text-blueGray-400 text-2xl"></i>
              <p className="text-blueGray-400 mt-2">Cargando contactos...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">{error}</p>
              <button
                onClick={loadContacts}
                className="mt-2 text-lightBlue-500 hover:text-lightBlue-600"
              >
                Reintentar
              </button>
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-8">
              <i className="fas fa-inbox text-blueGray-300 text-4xl mb-2"></i>
              <p className="text-blueGray-400">No se encontraron contactos</p>
            </div>
          ) : (
            <>
              <table className="items-center w-full bg-transparent border-collapse">
                <thead>
                  <tr>
                    <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                      Fecha
                    </th>
                    <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                      Contacto
                    </th>
                    <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                      Tipo Usuario
                    </th>
                    <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                      Tipo
                    </th>
                    <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                      Días
                    </th>
                    <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                      Mensaje
                    </th>
                    <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => (
                    <tr key={contact.id}>
                      <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                        <span className="text-blueGray-600">
                          {formatDate(contact.created_at)}
                        </span>
                      </td>
                      <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs p-4">
                        <div className="flex flex-col">
                          <span className="font-bold text-blueGray-700">
                            {contact.name || "—"}
                          </span>
                          <span className="text-blueGray-500">
                            {contact.email}
                          </span>
                        </div>
                      </td>
                      <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs p-4">
                        {getUserTypeBadge(contact.user_type)}
                      </td>
                      <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs p-4">
                        {getContactTypeBadge(contact.type)}
                      </td>
                      <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs p-4">
                        {getDaysSinceBadge(contact.days_since_contact)}
                      </td>
                      <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs p-4 max-w-xs">
                        <span className="text-blueGray-600">
                          {truncateMessage(contact.message)}
                        </span>
                      </td>
                      <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                        <button
                          className="text-lightBlue-500 hover:text-lightBlue-600 px-2"
                          onClick={() => onViewContact && onViewContact(contact)}
                          title="Ver detalles"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button
                          className="text-emerald-500 hover:text-emerald-600 px-2"
                          onClick={() => {
                            navigator.clipboard.writeText(contact.email);
                            alert('Email copiado al portapapeles');
                          }}
                          title="Copiar email"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-blueGray-100 flex justify-between items-center">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded text-sm ${
                      currentPage === 1
                        ? "bg-blueGray-200 text-blueGray-400 cursor-not-allowed"
                        : "bg-lightBlue-500 text-white hover:bg-lightBlue-600"
                    }`}
                  >
                    <i className="fas fa-chevron-left mr-1"></i>
                    Anterior
                  </button>
                  <span className="text-sm text-blueGray-600">
                    Página {currentPage} de {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded text-sm ${
                      currentPage === totalPages
                        ? "bg-blueGray-200 text-blueGray-400 cursor-not-allowed"
                        : "bg-lightBlue-500 text-white hover:bg-lightBlue-600"
                    }`}
                  >
                    Siguiente
                    <i className="fas fa-chevron-right ml-1"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

CardContactsTable.defaultProps = {
  color: "light",
};

CardContactsTable.propTypes = {
  color: PropTypes.oneOf(["light", "dark"]),
  onViewContact: PropTypes.func,
};
